import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { SxProps, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import { debounce } from 'lodash-es';
import { forwardRef } from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { useStores } from '~store';

import { LoadingSpinner } from '../Order/ordersDispatchStyledComponents';

const SEARCH_BAR_MAX_WIDTH_IN_PX = 180;

interface SearchBarProps {
  alwaysExpanded?: boolean;
  dataUi?: string;
  focusOnMount?: boolean;
  inputSx?: SxProps<Theme>;
  isLoading?: boolean;
  placeHolder?: string;
  setFilter?: (value: string) => void;
}

export type SearchBarHandler = {
  clear: () => void;
  focus: () => void;
};

export const SearchBar = forwardRef<SearchBarHandler, SearchBarProps>((props, ref) => {
  const theme = useTheme();
  const {
    alwaysExpanded,
    dataUi,
    focusOnMount = false,
    inputSx,
    isLoading,
    placeHolder,
    setFilter,
  } = props;
  const { userStore } = useStores();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isExpanded, setIsExpanded] = useState(Boolean(alwaysExpanded));
  const [inputValue, setInputValue] = useState('');

  const companyId = userStore?.currentCompanies?.[0].id || userStore?.userCompany?.id;

  useEffect(() => {
    setInputValue('');
    if (!alwaysExpanded) {
      setIsExpanded(false);
    }
  }, [companyId]);

  const inputHasValue = inputValue.length > 0;
  const applyFilter = debounce(() => {
    setFilter?.(inputValue);
  }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS);

  useEffect(() => {
    if (!isExpanded) {
      setInputValue('');
    }
  }, [isExpanded]);

  useEffect(() => {
    applyFilter();

    return () => {
      applyFilter.cancel();
    };
  }, [inputValue]);

  useEffect(() => {
    if (inputRef.current && focusOnMount) {
      inputRef.current.focus();
    }
  }, [focusOnMount]);

  useImperativeHandle(
    ref,
    () => ({
      clear() {
        setInputValue('');
      },
      focus() {
        inputRef.current?.focus();
      },
    }),
    [],
  );

  return (
    <Collapse
      collapsedSize={`calc(18px + (${theme.spacing(1)} * 2)`}
      in={isExpanded}
      orientation="horizontal"
      sx={{ '& .MuiCollapse-wrapperInner': { width: '100%' } }}
    >
      <Box
        onClick={() => {
          setIsExpanded(true);
          inputRef.current?.focus();
        }}
        sx={{
          borderRadius: theme.brandV2.borderRadius,
          maxWidth: alwaysExpanded ? undefined : SEARCH_BAR_MAX_WIDTH_IN_PX,
          width: '100%',
        }}
      >
        <OutlinedInput
          inputRef={inputRef}
          fullWidth
          inputProps={dataUi ? { 'data-ui': dataUi } : undefined}
          onBlur={() => {
            if (!inputHasValue && !alwaysExpanded) {
              setIsExpanded(false);
            }
          }}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeHolder}
          size="small"
          value={inputValue}
          startAdornment={
            <InputAdornment position="start">
              <Search
                sx={{
                  color: theme.brandV2.colors.treadBlack,
                  cursor: 'pointer',
                  fontSize: '16px',
                }}
              />
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment
              position="end"
              onClick={() => !isLoading && setInputValue('')}
            >
              {isLoading ? (
                <LoadingSpinner loadingIndicatorSize={16} isVisible={isLoading} />
              ) : inputHasValue ? (
                <Close
                  sx={{
                    color: theme.brandV2.colors.treadBlack,
                    cursor: 'pointer',
                    fontSize: '16px',
                  }}
                />
              ) : null}
            </InputAdornment>
          }
          sx={{
            '&.MuiInputBase-root': {
              height: `calc(${NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX}px - 2px)`,
              px: 1,
              py: 0.5,
              ...(inputSx && '&.MuiInputBase-root' in inputSx
                ? (inputSx?.['&.MuiInputBase-root'] as object)
                : {}),
              '.MuiInputBase-input': {
                fontSize: '12px',
                p: 0,
              },
              '.MuiOutlinedInput-notchedOutline': {
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: `1px solid ${theme.brandV2.colors.treadGray3}`,
              },
            },
          }}
        />
      </Box>
    </Collapse>
  );
});

SearchBar.displayName = 'SearchBar';
